import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Flex } from "@chakra-ui/react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { AddIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { equipmentsService } from "services/equipmentsService";
import { ROUTES } from "constants/routes-path";

function EquipmentsList() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState({ orderBy: "numero_serie" });
  const [response, setResponse] = useState(undefined);
  const [selectedRow, setSelectedRow] = useState(undefined);
  const dialogRef = useRef(undefined);
  const showToast = useCustomToast();
  const navigate = useNavigate();

  const columns = useMemo(() => [
    { Header: t("settings.equipment.list.table.header.serial-number"), accessor: "numero_serie" },
    { Header: t("settings.equipment.list.table.header.type"), accessor: "type_description" },
    { 
      Header: t("settings.equipment.list.table.header.active"),
      accessor: "ativo",
      Cell: ({ value }) => (value ? t("system.labels.true") : t("system.labels.false"))
    },
    { Header: t("settings.equipment.list.table.header.address"), accessor: "endereco" },
    { Header: t("settings.equipment.list.table.header.certificate"), accessor: "certificado_afericao" },
    { Header: t("settings.equipment.list.table.header.verification-date"), accessor: "data_verificacao" },
    { Header: t("settings.equipment.list.table.header.ait-number"), accessor: "numero_ait_inicial" },
    { Header: t("settings.equipment.list.table.header.imei"), accessor: "imei" },
    {
      Header: t("system.labels.actions"),
      disableSortBy: true,
      actions: [
        {
          label: t("system.actions.edit"),
          permission: "editar-pessoa",
          icon: <FaEdit />,
          onClick: ({ original }) => navigate(ROUTES.equipmentsEditPath.replace(":id", original.id)),
        },
        {
          label: t("system.actions.remove"),
          permission: "remover-pessoa",
          icon: <FaTrashAlt color="#E53E3E" />,
          onClick: ({ original }) => handleRemove(original),
        }
      ],
    },
  ], []);

  const newEquipment = () => {
    navigate(ROUTES.equipmentsAddPath);
  }

  const handleRemove = (row) => {
    setSelectedRow(row);
    dialogRef.current?.displayDialog({
      title: t("settings.equipment.list.actions.remove-equipment"),
      message: t("system.actions.remove-confirm"),
    });
  };

  const handleConfirmRemove = async () => {
    try {
      await equipmentsService.delete(selectedRow.id);
      showToast({ title: t("settings.equipment.messages.success.delete"), status: "success" });
      await fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    }
  };

  const fetchData = async (params = {}) => {
    if (response) setResponse(undefined);

    try {
      params = { ...searchParams, ...params };
      setSearchParams(params);
      const data = await equipmentsService.list(params);
      setResponse(data);
    } catch (err) {
      setResponse(getEmptyPageResponse(searchParams));
      showToast({ title: t(err), status: "error" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Flex gap="5" direction="column" pt={{ base: "150px", lg: "90px" }}>
      <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
        <Button
          variant="list-primary"
          leftIcon={<AddIcon fontSize="sm" />}
          alignSelf={{ sm: "flex-start", lg: undefined }}
          onClick={newEquipment}
        >
          {t("settings.equipment.list.actions.new-equipment")}
        </Button>
      </Flex>

      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <ListPageCardHeader title={t("settings.equipment.title")} subtitle={t("settings.equipment.description")} />

        <CardBody>
          {response ? (
            <SearchTable2
              columnsData={columns}
              tableData={response.content}
              pageableData={toTablePageableData(response, { defaultField: "numero_serie", ...searchParams })}
              onChange={fetchData}
            />
          ) : (
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          )}
        </CardBody>
      </Card>

      <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
    </Flex>
  );
}

export default EquipmentsList;
